import React, {useState, useRef, useEffect} from 'react'
import SparkMD5 from 'spark-md5'
import {Modal, Button} from 'antd'
import {ObsKeyToURL} from 'api/utils'

interface Point {
  x: number
  y: number
}

type Direction = 'nw' | 'ne' | 'sw' | 'se'
type MouseAction = '' | 'scale' | 'move'
class Box {
  top: number
  left: number
  width: number
  height: number

  constructor() {
    this.top = 0
    this.left = 0
    this.width = 0
    this.height = 0
  }

  set(top: number, left: number, width: number, height: number) {
    this.top = top
    this.left = left
    this.width = width
    this.height = height
  }

  move(dx: number, dy: number) {
    this.top = this.top + dy
    this.left = this.left + dx
  }

  copy(): Box {
    let c = new Box()
    c.set(this.top, this.left, this.width, this.height)
    return c
  }

  dragScale(direction: Direction, dy: number) {
    let dx = dy * 6.18
    switch (direction) {
      case 'nw':
        this.top = this.top + dy
        this.left = this.left + dx

        this.width = this.width - dx
        this.height = this.height - dy

        break

      case 'ne':
        this.top = this.top + dy
        this.height = this.height - dy

        this.width = this.width - dx

        break

      case 'sw':
        this.height = this.height + dy

        this.left = this.left - dx
        this.width = this.width + dx
        break

      case 'se':
        this.width = this.width + dx
        this.height = this.height + dy
        break
    }

    this.width = this.width > 0 ? this.width : 0
    this.height = this.height > 0 ? this.height : 0
  }

  contains(box: Box): boolean {
    return (
      this.top <= box.top &&
      this.left <= box.left &&
      this.top + this.height >= box.top + box.height &&
      this.left + this.width >= box.left + box.width
    )
  }
}

interface Props {
  showLogoModal: boolean
  setShowLogoModal: (show: boolean) => void
  initURL: string
  setURL: (url: string) => void
}

const newPoint = (): Point => ({
  x: -1,
  y: -1,
})

const UploadLogoCom: React.FC<Props> = ({showLogoModal, setShowLogoModal, initURL, setURL}) => {
  const [mouseAction, setMouseAction] = useState<MouseAction>('')
  const [previewURL, setPreviewURL] = useState('')
  const [startPoint, setStartPoint] = useState<Point>(newPoint())
  const inputRef = useRef<HTMLInputElement | null>(null)
  const dragDivRef = useRef<HTMLDivElement | null>(null)
  const containerCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [dragBox, setDragBox] = useState(new Box())

  const cancelUploadLogo = () => {
    setShowLogoModal(false)
  }

  //初始化渲染图片
  useEffect(() => {
    if (showLogoModal && initURL !== '') {
      setPreviewURL(ObsKeyToURL(initURL))
    }
  }, [initURL, showLogoModal])

  useEffect(() => {
    if (showLogoModal && previewURL !== '' && containerRef && containerRef.current) {
      let tmp: Box = new Box()
      let divStyle = getComputedStyle(containerRef.current)
      tmp.set(
        parseInt(divStyle.top),
        parseInt(divStyle.left),
        parseInt(divStyle.width),
        parseInt(divStyle.height)
      )
      setContainerBox(tmp)

      if (dragDivRef.current) {
        let dragStyle = dragDivRef.current.style
        dragDivRef.current.style.height = 100 + 'px'
        dragDivRef.current.style.width = 618 + 'px'
        dragDivRef.current.style.left = 0 + 'px'
        dragDivRef.current.style.top = 0 + 'px'
        let tmpDragBox = dragBox
        tmpDragBox.set(
          parseInt(dragStyle.top),
          parseInt(dragStyle.left),
          parseInt(dragStyle.width),
          parseInt(dragStyle.height)
        )

        setDragBox(tmpDragBox)

        let img = new Image()
        img.onload = () => {
          drawImage(img)
        }
        if (previewURL.startsWith('http')) {
          img.src = previewURL + '?' + new Date().getTime()
        } else {
          img.src = previewURL
        }
        img.setAttribute('crossOrigin', '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewURL, showLogoModal])

  const drawImage = previewImage => {
    if (containerCanvasRef.current) {
      let context = containerCanvasRef.current.getContext('2d')
      let width = containerCanvasRef.current.width
      let height = containerCanvasRef.current.height
      if (context) {
        context.clearRect(0, 0, width, height)
        let ratio = previewImage.naturalWidth / previewImage.naturalHeight
        let imgX = 0
        let imgY = 0
        let newWidth: number
        let newHeight: number
        if (ratio > width / height) {
          newWidth = width
          newHeight = newWidth / ratio
          imgY = (height - newHeight) / 2
          context.drawImage(previewImage, imgX, imgY, newWidth, newHeight)
        } else {
          newHeight = height
          newWidth = newHeight * ratio
          imgX = (width - newWidth) / 2
          context.drawImage(previewImage, imgX, imgY, newWidth, newHeight)
        }
      }
    }
  }

  //裁剪图片
  const submitUploadLogo = () => {
    if (containerRef.current && containerCanvasRef.current) {
      let canvas = containerCanvasRef.current
      let ctx = canvas.getContext('2d')
      let LTX = dragBox.left
      let LTY = dragBox.top
      if (ctx) {
        let data = ctx.getImageData(LTX, LTY, dragBox.width, dragBox.height)
        let nc = document.createElement('canvas')
        nc.width = 618
        nc.height = 100
        nc.getContext('2d')?.putImageData(data, 0, 0)
        let file = dataURLtoFile(nc.toDataURL(), 'school_logo.png')
        fileMd5(file)
        setShowLogoModal(false)
      }
    }
  }

  //上传华为云
  const fileMd5 = (file: File) => {
    let chunkSize = 2097152 // read in chunks of 2MB
    let fileIndex = 0
    let spark = new SparkMD5.ArrayBuffer()
    let fileReader = new FileReader()

    fileReader.onload = function (e) {
      let result = e.target?.result
      if (result && typeof result !== 'string') {
        spark.append(result)
      }
      if (fileIndex < file.size) {
        fileIndex = fileIndex + chunkSize
        loadNext()
      } else {
        let key = spark.end()
        if (urlChanged(key)) {
          upload('org/image/' + key, file)
        }
      }
    }

    const loadNext = () => {
      const slice = file.slice(fileIndex, fileIndex + chunkSize)
      fileReader.readAsArrayBuffer(slice)
    }

    // 启动读取文件
    loadNext()
  }

  const urlChanged = (key: string): Boolean => {
    let arr = initURL.split('/')
    let oldKey = ''
    if (arr.length > 0) {
      oldKey = arr[-1]
    }

    return oldKey !== key
  }

  const getObsClient = () => {
    let ak = process.env.REACT_APP_HW_AK
    let sk = process.env.REACT_APP_HW_SK
    let server = process.env.REACT_APP_HW_SERVER

    return new ObsClient({
      access_key_id: ak,
      secret_access_key: sk,
      server: server,
      timeout: 60 * 5,
    })
  }

  const upload = (key: string, file: File) => {
    let bucket = process.env.REACT_APP_HW_BUCKET

    getObsClient()
      .putObject({
        Bucket: bucket,
        Key: key,
        SourceFile: file,
      })
      .then(res => {
        setURL(key)
      })
  }

  //dataURL转化为File
  const dataURLtoFile = (dataurl, filename) => {
    let arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1]
    let bstr = atob(arr[1])
    let n = bstr.length
    let u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, {type: mime})
  }

  // //画布转为图片
  // const canvasToImage = canvas => {
  //   let image = new Image()
  //   image.src = canvas.toDataURL('image/png')
  //   return image.src
  // }

  const triggerInput = () => {
    inputRef.current?.click()
  }

  const onFileChange = e => {
    if (e.target.files.length <= 0) {
      return
    }
    let file = e.target.files[0]
    setPreviewURL(getPreURL(file))
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const getPreURL = (file: File) => {
    let windowURL = window.URL || window.webkitURL
    let src = windowURL.createObjectURL(file)
    return src
  }

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerBox, setContainerBox] = useState<Box>(new Box())

  // 开始拖动
  const startMove = e => {
    // 缩放优先
    if (mouseAction === 'scale') {
      return
    }
    setMouseAction('move')
    setStartPoint({x: e.clientX, y: e.clientY})
  }

  const afterMove = e => {
    setMouseAction('')
  }

  const moveMouse = e => {
    if (!dragDivRef.current) {
      return
    }

    let dx = e.clientX - startPoint.x
    let dy = e.clientY - startPoint.y
    let nBox = dragBox.copy()

    switch (mouseAction) {
      case 'move': // 拖动
        nBox.move(dx, dy)
        handleBoundary(nBox)
        break
      case 'scale': // 缩放
        nBox.dragScale(scaleDirection, dy)
        break
      default:
        return
    }
    if (containerBox.contains(nBox)) {
      dragDivRef.current.style.height = nBox.height + 'px'
      dragDivRef.current.style.width = nBox.width + 'px'
      dragDivRef.current.style.left = nBox.left + 'px'
      dragDivRef.current.style.top = nBox.top + 'px'
    }
  }

  // 停止拖动
  useEffect(() => {
    if (mouseAction === '' && dragDivRef.current) {
      let st = dragDivRef.current.style
      let db = new Box()
      db.set(parseInt(st.top), parseInt(st.left), parseInt(st.width), parseInt(st.height))
      setDragBox(db)
    }
  }, [mouseAction])

  const [scaleDirection, setScaleDirection] = useState<Direction>('nw')

  // 点击端点
  const startScale = (direction: Direction, e) => {
    e.stopPropagation()
    setMouseAction('scale')
    setScaleDirection(direction)
    setStartPoint({x: e.clientX, y: e.clientY})
  }

  // 超出边界
  const leaveContainer = () => {
    setMouseAction('')
  }

  //处理边界吸附
  const handleBoundary = (nBox: Box) => {
    if (containerRef.current) {
      let containerClientWidth = containerRef.current?.clientWidth
      let containerClientHeight = containerRef.current?.clientHeight
      if (nBox.top < 4 && nBox.left < 4) {
        //左上角
        nBox.top = 0
        nBox.left = 0
      } else if (containerClientWidth - (nBox.left + nBox.width) < 4 && nBox.top < 4) {
        //右上角
        nBox.top = 0
        nBox.left = containerClientWidth - nBox.width
      } else if (containerClientHeight - (nBox.top + nBox.height) < 4 && nBox.left < 4) {
        //左下角
        nBox.left = 0
        nBox.top = containerClientHeight - nBox.height
      } else if (
        containerClientHeight - (nBox.top + nBox.height) < 4 &&
        containerClientWidth - (nBox.left + nBox.width) < 4
      ) {
        //右下角
        nBox.left = containerClientWidth - nBox.width
        nBox.top = containerClientHeight - nBox.height
      } else if (nBox.top < 4) {
        //上
        nBox.top = 0
      } else if (nBox.left < 4) {
        //左
        nBox.left = 0
      } else if (containerClientWidth - (nBox.left + nBox.width) < 4) {
        //右
        nBox.left = containerClientWidth - nBox.width
      } else if (containerClientHeight - (nBox.top + nBox.height) < 4) {
        //下
        nBox.top = containerClientHeight - nBox.height
      }
    }
  }
  return (
    <>
      <Modal
        width={682}
        title="上传logo"
        visible={showLogoModal}
        centered
        onOk={submitUploadLogo}
        onCancel={cancelUploadLogo}
        className="upload-logo-modal"
        maskClosable={false}
      >
        <Button onClick={triggerInput}>本地上传</Button>
        {previewURL === '' ? (
          <div className="upload-logo-body-null">
            <div className="second-title">宽高比为309：50</div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="upload-logo-body"
            style={{userSelect: 'none'}}
            onMouseMove={moveMouse}
            onMouseUp={afterMove}
            onMouseLeave={leaveContainer}
          >
            <canvas width="618" height="100" ref={containerCanvasRef}></canvas>

            <div
              className="crop-box"
              style={{left: 0, top: 0, width: 618, height: 100}}
              ref={dragDivRef}
              onMouseDown={startMove}
            >
              <div className="crop-container">
                <div className="handle nw" onMouseDown={e => startScale('nw', e)}></div>
                <div className="handle ne" onMouseDown={e => startScale('ne', e)}></div>
                <div className="handle sw" onMouseDown={e => startScale('sw', e)}></div>
                <div className="handle se" onMouseDown={e => startScale('se', e)}></div>
              </div>
            </div>
          </div>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg"
          className="hide"
          ref={inputRef}
          onChange={onFileChange}
        />
      </Modal>
    </>
  )
}

export default UploadLogoCom
